import React, { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { 
 Building2, 
 DollarSign, 
 Plus,
 Calendar,
 Percent,
 Clock,
 CheckCircle,
 AlertTriangle,
 CreditCard,
 TrendingDown,
 FileText,
 ChevronDown,
 ChevronUp,
 Trash2,
 Edit,
 Banknote,
 Receipt
} from"lucide-react";
import { toast } from"sonner";

const LoansPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [loans, setLoans] = useState([]);
 const [summary, setSummary] = useState(null);
 const [projects, setProjects] = useState([]);
 const [showCreateDialog, setShowCreateDialog] = useState(false);
 const [showEditDialog, setShowEditDialog] = useState(false);
 const [showPaymentDialog, setShowPaymentDialog] = useState(false);
 const [selectedLoan, setSelectedLoan] = useState(null);
 const [expandedLoans, setExpandedLoans] = useState({});
 const [formData, setFormData] = useState({
 bank_name:"",
 loan_type:"business",
 loan_number:"",
 project_id:"",
 principal_amount:"",
 interest_rate:"",
 interest_type:"fixed",
 origination_fee_percent:"0",
 grace_period_months:"0",
 loan_term_months:"",
 start_date: new Date().toISOString().split('T')[0],
 payment_day:"1",
 currency:"EUR",
 collateral:"",
 guarantor:"",
 notes:""
 });
 const [editData, setEditData] = useState({
 bank_name:"",
 loan_type:"",
 loan_number:"",
 interest_rate:"",
 interest_type:"",
 origination_fee_percent:"",
 grace_period_months:"",
 collateral:"",
 guarantor:"",
 notes:""
 });
 const [paymentData, setPaymentData] = useState({
 payment_number:"",
 paid_amount:"",
 paid_date: new Date().toISOString().split('T')[0],
 is_extra_payment: false,
 apply_to_principal: true,
 notes:""
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 setLoading(true);
 try {
 const [loansRes, summaryRes, projectsRes] = await Promise.all([
 axios.get("/loans"),
 axios.get("/loans/summary/overview"),
 axios.get("/projects")
 ]);
 setLoans(loansRes.data);
 setSummary(summaryRes.data);
 setProjects(projectsRes.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleCreateLoan = async () => {
 if (!formData.bank_name || !formData.principal_amount || !formData.interest_rate || !formData.loan_term_months) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 await axios.post("/loans", {
 ...formData,
 principal_amount: parseFloat(formData.principal_amount),
 interest_rate: parseFloat(formData.interest_rate),
 origination_fee_percent: parseFloat(formData.origination_fee_percent || 0),
 grace_period_months: parseInt(formData.grace_period_months || 0),
 loan_term_months: parseInt(formData.loan_term_months),
 payment_day: parseInt(formData.payment_day),
 start_date: new Date(formData.start_date).toISOString(),
 project_id: formData.project_id ==="none"|| !formData.project_id ? null : formData.project_id
 });
 toast.success(t("messages.saveSuccess"));
 setShowCreateDialog(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create loan");
 }
 };

 const handleEditLoan = async () => {
 if (!editData.bank_name) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 const updateData = {};
 if (editData.bank_name) updateData.bank_name = editData.bank_name;
 if (editData.loan_type) updateData.loan_type = editData.loan_type;
 if (editData.loan_number !== undefined) updateData.loan_number = editData.loan_number;
 if (editData.interest_rate) updateData.interest_rate = parseFloat(editData.interest_rate);
 if (editData.interest_type) updateData.interest_type = editData.interest_type;
 if (editData.origination_fee_percent !== undefined) updateData.origination_fee_percent = parseFloat(editData.origination_fee_percent || 0);
 if (editData.collateral !== undefined) updateData.collateral = editData.collateral;
 if (editData.guarantor !== undefined) updateData.guarantor = editData.guarantor;
 if (editData.notes !== undefined) updateData.notes = editData.notes;

 await axios.patch(`/loans/${selectedLoan.id}`, updateData);
 toast.success(t("messages.updateSuccess"));
 setShowEditDialog(false);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to update loan");
 }
 };

 const handleRecordPayment = async () => {
 if (!paymentData.is_extra_payment && !paymentData.payment_number) {
 toast.error(t("validation.required"));
 return;
 }
 if (!paymentData.paid_amount || parseFloat(paymentData.paid_amount) <= 0) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 await axios.post(`/loans/${selectedLoan.id}/payment`, {
 payment_number: paymentData.is_extra_payment ? null : parseInt(paymentData.payment_number),
 paid_amount: parseFloat(paymentData.paid_amount),
 paid_date: new Date(paymentData.paid_date).toISOString(),
 is_extra_payment: paymentData.is_extra_payment,
 apply_to_principal: paymentData.apply_to_principal,
 notes: paymentData.notes || null
 });
 toast.success(t("messages.saveSuccess"));
 setShowPaymentDialog(false);
 setPaymentData({
 payment_number:"",
 paid_amount:"",
 paid_date: new Date().toISOString().split('T')[0],
 is_extra_payment: false,
 apply_to_principal: true,
 notes:""
 });
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to record payment");
 }
 };

 const handleDeleteLoan = async (loanId) => {
 if (!window.confirm("Are you sure you want to delete this loan record?")) return;
 
 try {
 await axios.delete(`/loans/${loanId}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to delete loan");
 }
 };

 const resetForm = () => {
 setFormData({
 bank_name:"",
 loan_type:"business",
 loan_number:"",
 project_id:"",
 principal_amount:"",
 interest_rate:"",
 interest_type:"fixed",
 origination_fee_percent:"0",
 grace_period_months:"0",
 loan_term_months:"",
 start_date: new Date().toISOString().split('T')[0],
 payment_day:"1",
 currency:"EUR",
 collateral:"",
 guarantor:"",
 notes:""
 });
 };

 const openEditDialog = (loan) => {
 setSelectedLoan(loan);
 setEditData({
 bank_name: loan.bank_name ||"",
 loan_type: loan.loan_type ||"",
 loan_number: loan.loan_number ||"",
 interest_rate: loan.interest_rate?.toString() ||"",
 interest_type: loan.interest_type ||"fixed",
 origination_fee_percent: loan.origination_fee_percent?.toString() ||"0",
 grace_period_months: loan.grace_period_months?.toString() ||"0",
 collateral: loan.collateral ||"",
 guarantor: loan.guarantor ||"",
 notes: loan.notes ||""
 });
 setShowEditDialog(true);
 };

 const openPaymentDialog = (loan) => {
 setSelectedLoan(loan);
 // Find next unpaid payment
 const nextPayment = loan.payment_schedule?.find(p => !p.paid);
 if (nextPayment) {
 setPaymentData({
 payment_number: nextPayment.payment_number.toString(),
 paid_amount: nextPayment.total_payment.toString(),
 paid_date: new Date().toISOString().split('T')[0],
 is_extra_payment: false,
 apply_to_principal: true,
 notes:""
 });
 } else {
 setPaymentData({
 payment_number:"",
 paid_amount:"",
 paid_date: new Date().toISOString().split('T')[0],
 is_extra_payment: true,
 apply_to_principal: true,
 notes:""
 });
 }
 setShowPaymentDialog(true);
 };

 const toggleLoanExpand = (loanId) => {
 setExpandedLoans(prev => ({
 ...prev,
 [loanId]: !prev[loanId]
 }));
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return"-";
 return new Date(dateStr).toLocaleDateString();
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case"active":
 return <Badge className="bg-blue-500/10 text-blue-700 border-blue-200/60">Active</Badge>;
 case"paid_off":
 return <Badge className="bg-emerald-500/10 text-emerald-700 border-green-200/60">Paid Off</Badge>;
 case"defaulted":
 return <Badge className="bg-rose-500/10 text-rose-700 border-red-200/60">Defaulted</Badge>;
 case"refinanced":
 return <Badge className="bg-violet-500/10 text-violet-700 border-purple-200">Refinanced</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 const getLoanTypeBadge = (type) => {
 const colors = {
 mortgage:"bg-indigo-100 text-indigo-800",
 business:"bg-emerald-100 text-emerald-800",
 construction:"bg-amber-100 text-amber-800",
 line_of_credit:"bg-cyan-100 text-cyan-800",
 other:"bg-white/60 text-slate-800"
 };
 return <Badge className={colors[type] || colors.other}>{type.replace("_","")}</Badge>;
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div className="flex justify-between items-start">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 Loan Management
 </h1>
 <p className="text-slate-600 mt-1">
 Track company loans, payments, and interest
 </p>
 </div>
 <Button onClick={() => setShowCreateDialog(true)} data-testid="add-loan-btn">
 <Plus className="w-4 h-4 mr-2"/>
 Add Loan
 </Button>
 </div>

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-3 lg:grid-cols-7 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600">Active Loans</p>
 <p className="text-2xl font-bold text-slate-900">{summary.active_loans}</p>
 </div>
 <Banknote className="w-8 h-8 text-slate-400"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Original Principal</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_principal)}</p>
 <p className="text-xs text-gray-400 mt-1">Total borrowed</p>
 </div>
 <DollarSign className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Outstanding Balance</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_remaining_balance)}</p>
 <p className="text-xs text-gray-400 mt-1">Still owed</p>
 </div>
 <TrendingDown className="h-7 w-7 text-rose-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Principal Paid</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_principal - summary.total_remaining_balance)}</p>
 <p className="text-xs text-gray-400 mt-1">{summary.total_principal > 0 ? ((summary.total_principal - summary.total_remaining_balance) / summary.total_principal * 100).toFixed(1) : 0}% complete</p>
 </div>
 <CheckCircle className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Monthly Payments</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_monthly_payments)}</p>
 <p className="text-xs text-gray-400 mt-1">Combined monthly</p>
 </div>
 <Calendar className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Original Interest</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.original_total_interest)}</p>
 <p className="text-xs text-gray-400 mt-1">If paid on schedule</p>
 </div>
 <Percent className="h-7 w-7 text-violet-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500">Interest Paid</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.actual_interest_paid || 0)}</p>
 <p className="text-xs text-gray-400 mt-1">
 {summary.original_total_interest > 0 
 ? `${((summary.actual_interest_paid || 0) / summary.original_total_interest * 100).toFixed(1)}% of original`
 :"Actual paid"
 }
 </p>
 </div>
 <Receipt className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Upcoming Payments */}
 {summary?.upcoming_payments?.length > 0 && (
 <Card className="">
 <CardHeader className="pb-2">
 <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
 <AlertTriangle className="h-6 w-6 text-amber-600"/>
 Upcoming Payments
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex gap-4 overflow-x-auto pb-2">
 {summary.upcoming_payments.map((payment, idx) => (
 <div key={`${payment.loan_id}-${idx}`} className="flex-shrink-0 bg-white rounded-sm p-3 border border-slate-200 min-w-[200px]">
 <p className="font-medium text-slate-900">{payment.bank_name}</p>
 <p className="text-sm text-slate-600">Due: {formatDate(payment.due_date)}</p>
 <p className="text-lg font-bold text-slate-900">{formatCurrency(payment.amount, payment.currency)}</p>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Loans List */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Building2 className="w-5 h-5 text-indigo-600"/>
 All Loans
 </CardTitle>
 </CardHeader>
 <CardContent>
 {loans.length === 0 ? (
 <div className="text-center py-12">
 <Banknote className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600">No loans recorded yet</p>
 <Button className="mt-4"onClick={() => setShowCreateDialog(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 Add First Loan
 </Button>
 </div>
 ) : (
 <div className="space-y-4">
 {loans.map((loan) => (
 <div key={loan.id} className="border border-slate-200 rounded-sm overflow-hidden">
 {/* Loan Header */}
 <div className="p-4 bg-white/50 flex items-center justify-between">
 <div className="flex items-center gap-4">
 <Building2 className="h-7 w-7 text-slate-600"/>
 <div>
 <div className="flex items-center gap-2">
 <h3 className="font-semibold text-slate-900">{loan.bank_name}</h3>
 {getLoanTypeBadge(loan.loan_type)}
 {getStatusBadge(loan.status)}
 </div>
 <p className="text-sm text-slate-500">
 {loan.loan_number && `Ref: ${loan.loan_number} • `}
 Started {formatDate(loan.start_date)}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-3">
 <div className="text-right">
 <p className="text-sm text-slate-500">Remaining Balance</p>
 <p className="text-xl font-bold text-slate-900">{formatCurrency(loan.remaining_balance, loan.currency)}</p>
 </div>
 <div className="flex gap-2">
 {loan.status ==="active"&& (
 <Button size="sm"variant="outline"className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"onClick={() => openPaymentDialog(loan)} data-testid={`record-payment-${loan.id}`}>
 <CreditCard className="w-4 h-4 mr-1"/>
 Record Payment
 </Button>
 )}
 <Button 
 variant="outline"
 size="sm"
 onClick={() => openEditDialog(loan)}
 data-testid={`edit-loan-${loan.id}`}
 >
 <Edit className="w-4 h-4 mr-1"/>
 Edit
 </Button>
 <Button 
 variant="ghost"
 size="sm"
 onClick={() => toggleLoanExpand(loan.id)}
 >
 {expandedLoans[loan.id] ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
 </Button>
 {user.role !=="accountant"&& (
 <Button 
 variant="ghost"
 size="sm"
 className="text-red-600 hover:text-red-700"
 onClick={() => handleDeleteLoan(loan.id)}
 >
 <Trash2 className="w-4 h-4"/>
 </Button>
 )}
 </div>
 </div>
 </div>

 {/* Expanded Details */}
 {expandedLoans[loan.id] && (
 <div className="p-4 border-t border-slate-200">
 <div className="grid grid-cols-4 gap-6 mb-6">
 <div>
 <p className="text-sm text-slate-500">Principal Amount</p>
 <p className="font-semibold text-slate-900">{formatCurrency(loan.principal_amount, loan.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Interest Rate</p>
 <p className="font-semibold text-slate-900">{loan.interest_rate}% ({loan.interest_type})</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Origination Fee</p>
 <p className="font-semibold text-slate-900">
 {loan.origination_fee_percent > 0 
 ? `${loan.origination_fee_percent}% (${formatCurrency(loan.origination_fee_amount, loan.currency)})`
 :"None"
 }
 </p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Loan Term</p>
 <p className="font-semibold text-slate-900">{loan.loan_term_months} months</p>
 </div>
 {loan.grace_period_months > 0 && (
 <div>
 <p className="text-sm text-slate-500">Grace Period</p>
 <p className="font-semibold text-amber-600">{loan.grace_period_months} months</p>
 </div>
 )}
 {loan.first_payment_date && loan.grace_period_months > 0 && (
 <div>
 <p className="text-sm text-slate-500">First Payment Due</p>
 <p className="font-semibold text-slate-900">{formatDate(loan.first_payment_date)}</p>
 </div>
 )}
 <div>
 <p className="text-sm text-slate-500">Monthly Payment</p>
 <p className="font-semibold text-slate-900">{formatCurrency(loan.monthly_payment, loan.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Total Interest</p>
 <p className="font-semibold text-red-600">{formatCurrency(loan.total_interest, loan.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Total Amount</p>
 <p className="font-semibold text-slate-900">{formatCurrency(loan.total_amount, loan.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Payments Made</p>
 <p className="font-semibold text-green-600">{loan.payments_made} / {loan.loan_term_months}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Next Payment</p>
 <p className="font-semibold text-slate-900">{formatDate(loan.next_payment_date)}</p>
 </div>
 {loan.extra_payments_total > 0 && (
 <div>
 <p className="text-sm text-slate-500">Extra Payments Made</p>
 <p className="font-semibold text-green-600">{formatCurrency(loan.extra_payments_total, loan.currency)}</p>
 </div>
 )}
 </div>

 {/* Additional Info */}
 {(loan.collateral || loan.guarantor || loan.notes) && (
 <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-white/50 rounded-sm">
 {loan.collateral && (
 <div>
 <p className="text-sm text-slate-500">Collateral</p>
 <p className="text-slate-900">{loan.collateral}</p>
 </div>
 )}
 {loan.guarantor && (
 <div>
 <p className="text-sm text-slate-500">Guarantor</p>
 <p className="text-slate-900">{loan.guarantor}</p>
 </div>
 )}
 {loan.notes && (
 <div className="col-span-3">
 <p className="text-sm text-slate-500">Notes</p>
 <p className="text-slate-900">{loan.notes}</p>
 </div>
 )}
 </div>
 )}

 {/* Payment Schedule */}
 <div>
 <h4 className="font-semibold text-slate-900 mb-3">Payment Schedule</h4>
 <div className="max-h-64 overflow-y-auto border rounded-sm">
 <table className="w-full text-sm">
 <thead className="bg-white/50 sticky top-0">
 <tr>
 <th className="text-left py-2 px-3">#</th>
 <th className="text-left py-2 px-3">Due Date</th>
 <th className="text-right py-2 px-3">Principal</th>
 <th className="text-right py-2 px-3">Interest</th>
 <th className="text-right py-2 px-3">Total</th>
 <th className="text-right py-2 px-3">Balance</th>
 <th className="text-center py-2 px-3">Status</th>
 </tr>
 </thead>
 <tbody>
 {loan.payment_schedule?.slice(0, 24).map((payment) => (
 <tr key={payment.payment_number} className="border-b border-white/40">
 <td className="py-2 px-3">{payment.payment_number}</td>
 <td className="py-2 px-3">{formatDate(payment.due_date)}</td>
 <td className="py-2 px-3 text-right">{formatCurrency(payment.principal_amount, loan.currency)}</td>
 <td className="py-2 px-3 text-right text-slate-900">{formatCurrency(payment.interest_amount, loan.currency)}</td>
 <td className="py-2 px-3 text-right font-medium text-slate-900">{formatCurrency(payment.total_payment, loan.currency)}</td>
 <td className="py-2 px-3 text-right">{formatCurrency(payment.remaining_balance, loan.currency)}</td>
 <td className="py-2 px-3 text-center">
 {payment.paid ? (
 <Badge className="bg-emerald-500/10 text-emerald-700 text-xs">Paid</Badge>
 ) : (
 <Badge variant="outline"className="text-xs text-slate-500">Pending</Badge>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 {loan.payment_schedule?.length > 24 && (
 <div className="text-center py-2 text-sm text-slate-500 bg-white/50">
 ... and {loan.payment_schedule.length - 24} more payments
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Create Loan Dialog */}
 <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Add New Loan</DialogTitle>
 </DialogHeader>
 <div className="grid grid-cols-2 gap-4 py-4">
 <div className="col-span-2">
 <Label>Bank Name *</Label>
 <Input
 value={formData.bank_name}
 onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
 placeholder="e.g., Alpha Bank"
 data-testid="loan-bank-name"
 />
 </div>
 <div>
 <Label>Loan Type *</Label>
 <Select value={formData.loan_type} onValueChange={(v) => setFormData({...formData, loan_type: v})}>
 <SelectTrigger data-testid="loan-type-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="mortgage">Mortgage</SelectItem>
 <SelectItem value="business">Business Loan</SelectItem>
 <SelectItem value="construction">Construction Loan</SelectItem>
 <SelectItem value="line_of_credit">Line of Credit</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Loan Reference Number</Label>
 <Input
 value={formData.loan_number}
 onChange={(e) => setFormData({...formData, loan_number: e.target.value})}
 placeholder="Bank's loan reference"
 />
 </div>
 <div>
 <Label>Link to Project (Optional)</Label>
 <Select value={formData.project_id} onValueChange={(v) => setFormData({...formData, project_id: v})}>
 <SelectTrigger>
 <SelectValue placeholder="Select project..."/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="none">No project</SelectItem>
 {projects.map(p => (
 <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Currency</Label>
 <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="EUR">EUR (€)</SelectItem>
 <SelectItem value="USD">USD ($)</SelectItem>
 <SelectItem value="MKD">MKD (ден)</SelectItem>
 <SelectItem value="ALL">ALL (L)</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Principal Amount *</Label>
 <Input
 type="number"
 value={formData.principal_amount}
 onChange={(e) => setFormData({...formData, principal_amount: e.target.value})}
 placeholder="Loan amount"
 data-testid="loan-principal"
 />
 </div>
 <div>
 <Label>Annual Interest Rate (%) *</Label>
 <Input
 type="number"
 step="0.1"
 value={formData.interest_rate}
 onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
 placeholder="e.g., 5.5"
 data-testid="loan-interest-rate"
 />
 </div>
 <div>
 <Label>Interest Type</Label>
 <Select value={formData.interest_type} onValueChange={(v) => setFormData({...formData, interest_type: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="fixed">Fixed Rate</SelectItem>
 <SelectItem value="variable">Variable Rate</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Origination Fee (%)</Label>
 <Input
 type="number"
 step="0.01"
 value={formData.origination_fee_percent}
 onChange={(e) => setFormData({...formData, origination_fee_percent: e.target.value})}
 placeholder="e.g., 0.2"
 data-testid="loan-origination-fee"
 />
 <p className="text-[11px] text-slate-500 mt-1">Bank's one-time fee when loan is disbursed</p>
 </div>
 <div>
 <Label>Loan Term (Months) *</Label>
 <Input
 type="number"
 value={formData.loan_term_months}
 onChange={(e) => setFormData({...formData, loan_term_months: e.target.value})}
 placeholder="e.g., 120 for 10 years"
 data-testid="loan-term"
 />
 </div>
 <div>
 <Label>Grace Period (Months)</Label>
 <Input
 type="number"
 value={formData.grace_period_months}
 onChange={(e) => setFormData({...formData, grace_period_months: e.target.value})}
 placeholder="e.g., 6"
 data-testid="loan-grace-period"
 />
 <p className="text-[11px] text-slate-500 mt-1">Months before first payment is due</p>
 </div>
 <div>
 <Label>Start Date *</Label>
 <Input
 type="date"
 value={formData.start_date}
 onChange={(e) => setFormData({...formData, start_date: e.target.value})}
 data-testid="loan-start-date"
 />
 </div>
 <div>
 <Label>Payment Day of Month</Label>
 <Select value={formData.payment_day} onValueChange={(v) => setFormData({...formData, payment_day: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {[1, 5, 10, 15, 20, 25, 28].map(day => (
 <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div className="col-span-2">
 <Label>Collateral Description</Label>
 <Input
 value={formData.collateral}
 onChange={(e) => setFormData({...formData, collateral: e.target.value})}
 placeholder="Property, equipment, etc."
 />
 </div>
 <div className="col-span-2">
 <Label>Guarantor Information</Label>
 <Input
 value={formData.guarantor}
 onChange={(e) => setFormData({...formData, guarantor: e.target.value})}
 placeholder="Guarantor name and details"
 />
 </div>
 <div className="col-span-2">
 <Label>Notes</Label>
 <Textarea
 value={formData.notes}
 onChange={(e) => setFormData({...formData, notes: e.target.value})}
 placeholder="Additional notes about this loan..."
 rows={3}
 />
 </div>
 </div>
 <DialogFooter>
 <Button variant="outline"onClick={() => setShowCreateDialog(false)}>Cancel</Button>
 <Button onClick={handleCreateLoan} data-testid="save-loan-btn">Create Loan</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Record Payment Dialog */}
 <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Record Loan Payment</DialogTitle>
 </DialogHeader>
 {selectedLoan && (
 <div className="py-4 space-y-4">
 <div className="p-3 bg-white/50 rounded-sm">
 <p className="text-sm text-slate-600">Loan: {selectedLoan.bank_name}</p>
 <div className="flex justify-between">
 <span className="font-semibold">Monthly Payment: {formatCurrency(selectedLoan.monthly_payment, selectedLoan.currency)}</span>
 <span className="text-sm text-slate-600">Remaining: {formatCurrency(selectedLoan.remaining_balance, selectedLoan.currency)}</span>
 </div>
 </div>
 
 {/* Extra Payment Toggle */}
 <div className="flex items-center gap-3 p-3 border rounded-sm">
 <input
 type="checkbox"
 id="extra-payment"
 checked={paymentData.is_extra_payment}
 onChange={(e) => setPaymentData({...paymentData, is_extra_payment: e.target.checked, payment_number:""})}
 className="w-4 h-4 rounded border-slate-200"
 />
 <label htmlFor="extra-payment"className="flex-1">
 <p className="font-medium text-slate-900">Extra/Lump Sum Payment</p>
 <p className="text-[11px] text-slate-500">Make a larger payment to reduce principal faster</p>
 </label>
 </div>
 
 {!paymentData.is_extra_payment && (
 <div>
 <Label>Scheduled Payment *</Label>
 <Select value={paymentData.payment_number} onValueChange={(v) => {
 const payment = selectedLoan.payment_schedule?.find(p => p.payment_number.toString() === v);
 setPaymentData({
 ...paymentData, 
 payment_number: v,
 paid_amount: payment ? payment.total_payment.toString() : paymentData.paid_amount
 });
 }}>
 <SelectTrigger>
 <SelectValue placeholder="Select payment..."/>
 </SelectTrigger>
 <SelectContent>
 {selectedLoan.payment_schedule?.filter(p => !p.paid).map(p => (
 <SelectItem key={p.payment_number} value={p.payment_number.toString()}>
 #{p.payment_number} - Due {formatDate(p.due_date)} ({formatCurrency(p.total_payment, selectedLoan.currency)})
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 )}
 
 <div>
 <Label>Amount Paid *</Label>
 <Input
 type="number"
 value={paymentData.paid_amount}
 onChange={(e) => setPaymentData({...paymentData, paid_amount: e.target.value})}
 placeholder={paymentData.is_extra_payment ?"Enter lump sum amount":"Payment amount (can be more than scheduled)"}
 />
 {!paymentData.is_extra_payment && (
 <p className="text-[11px] text-slate-500 mt-1">
 Pay more than scheduled to reduce principal faster
 </p>
 )}
 </div>
 
 {paymentData.is_extra_payment && (
 <div className="flex items-center gap-3 p-3 bg-green-50/80 border border-green-200/60 rounded-sm">
 <input
 type="checkbox"
 id="apply-principal"
 checked={paymentData.apply_to_principal}
 onChange={(e) => setPaymentData({...paymentData, apply_to_principal: e.target.checked})}
 className="w-4 h-4 rounded border-slate-200"
 />
 <label htmlFor="apply-principal"className="flex-1">
 <p className="font-medium text-green-800">Apply to Principal</p>
 <p className="text-xs text-green-600">Reduces the total loan balance immediately</p>
 </label>
 </div>
 )}
 
 <div>
 <Label>Payment Date</Label>
 <Input
 type="date"
 value={paymentData.paid_date}
 onChange={(e) => setPaymentData({...paymentData, paid_date: e.target.value})}
 />
 </div>
 <div>
 <Label>Notes</Label>
 <Textarea
 value={paymentData.notes}
 onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
 placeholder="Payment notes..."
 rows={2}
 />
 </div>
 </div>
 )}
 <DialogFooter>
 <Button variant="outline"onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
 <Button onClick={handleRecordPayment}>
 {paymentData.is_extra_payment ?"Record Extra Payment":"Record Payment"}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 {/* Edit Loan Dialog */}
 <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
 <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Edit Loan Details</DialogTitle>
 </DialogHeader>
 {selectedLoan && (
 <div className="grid grid-cols-2 gap-4 py-4">
 <div className="col-span-2">
 <Label>Bank Name *</Label>
 <Input
 value={editData.bank_name}
 onChange={(e) => setEditData({...editData, bank_name: e.target.value})}
 placeholder="e.g., Alpha Bank"
 />
 </div>
 <div>
 <Label>Loan Type</Label>
 <Select value={editData.loan_type} onValueChange={(v) => setEditData({...editData, loan_type: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="mortgage">Mortgage</SelectItem>
 <SelectItem value="business">Business Loan</SelectItem>
 <SelectItem value="construction">Construction Loan</SelectItem>
 <SelectItem value="line_of_credit">Line of Credit</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Loan Reference Number</Label>
 <Input
 value={editData.loan_number}
 onChange={(e) => setEditData({...editData, loan_number: e.target.value})}
 placeholder="Bank's loan reference"
 />
 </div>
 <div>
 <Label>Annual Interest Rate (%)</Label>
 <Input
 type="number"
 step="0.1"
 value={editData.interest_rate}
 onChange={(e) => setEditData({...editData, interest_rate: e.target.value})}
 placeholder="e.g., 5.5"
 />
 <p className="text-[11px] text-slate-500 mt-1">Update if bank adjusts rate</p>
 </div>
 <div>
 <Label>Interest Type</Label>
 <Select value={editData.interest_type} onValueChange={(v) => setEditData({...editData, interest_type: v})}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="fixed">Fixed Rate</SelectItem>
 <SelectItem value="variable">Variable Rate</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="col-span-2">
 <Label>Collateral Description</Label>
 <Input
 value={editData.collateral}
 onChange={(e) => setEditData({...editData, collateral: e.target.value})}
 placeholder="Property, equipment, etc."
 />
 </div>
 <div className="col-span-2">
 <Label>Guarantor Information</Label>
 <Input
 value={editData.guarantor}
 onChange={(e) => setEditData({...editData, guarantor: e.target.value})}
 placeholder="Guarantor name and details"
 />
 </div>
 <div className="col-span-2">
 <Label>Notes</Label>
 <Textarea
 value={editData.notes}
 onChange={(e) => setEditData({...editData, notes: e.target.value})}
 placeholder="Additional notes..."
 rows={3}
 />
 </div>
 </div>
 )}
 <DialogFooter>
 <Button variant="outline"onClick={() => setShowEditDialog(false)}>Cancel</Button>
 <Button onClick={handleEditLoan}>Save Changes</Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default LoansPage;
